import React, { useEffect, useState, useRef } from 'react';
import './Menu.css';
import Header from '../common/Header';
import { useNavigate } from 'react-router-dom';
import api from '../../config/axios';

interface RecipeRow { materialId: string; quantity: number | string; }

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  description?: string;
  imageUrl: string;
  category: 'cafe' | 'tea' | 'smoothie';
  ingredients?: { materialId: string; quantity: number }[];
  // Thuế suất riêng của món; null = dùng thuế mặc định của shop.
  taxRate?: number | null;
  // Thuế thực áp cho món (đã tính cả mức mặc định của shop), do backend trả về.
  effectiveTaxRate?: number;
}

interface Material { _id: string; name: string; unit: string; }

interface MenuProps {
  user: any;
}

const Menu: React.FC<MenuProps> = ({ user }) => {
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<MenuItem | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    image: null as File | null,
    category: 'cafe',
    taxRate: ''
  });
  const [filterCategory, setFilterCategory] = useState<'all' | 'cafe' | 'tea' | 'smoothie'>('all');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [recipe, setRecipe] = useState<RecipeRow[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const isAdmin = user.role === 'admin';

  const fetchMenus = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/menu');
      setMenus(res.data.data);
    } catch (err: any) {
      setError('Không thể tải menu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
    if (isAdmin) {
      api.get('/api/materials').then(res => setMaterials(res.data.data)).catch(() => {});
    }
  }, [isAdmin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, image: e.target.files![0] }));
    }
  };

  const openForm = (item?: MenuItem) => {
    if (item) {
      setEditItem(item);
      setFormData({
        name: item.name,
        price: item.price.toString(),
        description: item.description || '',
        image: null,
        category: item.category || 'cafe',
        // Bỏ trống = theo thuế mặc định của shop.
        taxRate: item.taxRate === undefined || item.taxRate === null ? '' : String(item.taxRate)
      });
      setRecipe((item.ingredients || []).map(i => ({ materialId: String(i.materialId), quantity: i.quantity })));
    } else {
      setEditItem(null);
      setFormData({ name: '', price: '', description: '', image: null, category: 'cafe', taxRate: '' });
      setRecipe([]);
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditItem(null);
    setFormData({ name: '', price: '', description: '', image: null, category: 'cafe', taxRate: '' });
    setRecipe([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addRecipeRow = () => setRecipe(prev => [...prev, { materialId: '', quantity: '' }]);
  const updateRecipeRow = (idx: number, field: keyof RecipeRow, value: string) =>
    setRecipe(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const removeRecipeRow = (idx: number) => setRecipe(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!editItem && !formData.image) {
      setError('Vui lòng chọn ảnh cho món mới');
      setLoading(false);
      return;
    }
    
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('price', String(Number(formData.price)));
      data.append('description', formData.description);
      data.append('category', formData.category);
      // Chuỗi rỗng = xóa thuế riêng, quay về thuế mặc định của shop.
      data.append('taxRate', formData.taxRate.trim());
      const cleanRecipe = recipe
        .filter(r => r.materialId && Number(r.quantity) > 0)
        .map(r => ({ materialId: r.materialId, quantity: Number(r.quantity) }));
      data.append('ingredients', JSON.stringify(cleanRecipe));
      if (formData.image) data.append('image', formData.image);
      
      if (editItem) {
        await api.put(`/api/menu/${editItem._id}`, data);
      } else {
        await api.post('/api/menu', data);
      }
      fetchMenus();
      closeForm();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Lỗi khi lưu món';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bạn chắc chắn muốn xóa món này?')) return;
    setLoading(true);
    setError('');
    try {
      await api.delete(`/api/menu/${id}`);
      fetchMenus();
    } catch (err: any) {
      setError('Lỗi khi xóa món');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const filteredMenus = filterCategory === 'all'
    ? menus
    : menus.filter(item => item.category === filterCategory);

  return (
    <>
      <Header user={user} onLogout={handleLogout}>
        <span className="user-role">
          {user.role === 'admin'
            ? 'Quản lý'
            : user.role === 'employee'
            ? 'Nhân viên'
            : 'Khách hàng'}
        </span>
      </Header>
      <div className="menu-container">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 28,
              cursor: 'pointer',
              marginRight: 12
            }}
            title="Quay lại màn hình chính"
          >
            ←
          </button>
          <h2 className="menu-title" style={{ margin: 0, flex: 1 }}>DANH SÁCH MENU</h2>
        </div>
        {user.role !== 'admin' && user.role !== 'employee' && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <button
              className="menu-reserve-btn"
              style={{ background: '#4caf50', color: '#fff', padding: '8px 20px', border: 'none', borderRadius: 4, fontSize: 16, cursor: 'pointer' }}
              onClick={() => navigate('/tables')}
            >
              Đặt bàn
            </button>
          </div>
        )}
        {isAdmin && (
          <button className="menu-add-btn" onClick={() => openForm()}>+ Thêm món</button>
        )}
        <div className="menu-filter-category">
          <label>Lọc loại: </label>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value as any)}>
            <option value="all">Tất cả</option>
            <option value="cafe">Cafe</option>
            <option value="tea">Trà</option>
            <option value="smoothie">Sinh tố</option>
          </select>
        </div>
        {loading && <div>Đang tải...</div>}
        {error && <div className="error-message">{error}</div>}
        <div className="menu-list-grid">
          {filteredMenus.map(item => (
            <div className="menu-card" key={item._id}>
              <div className="menu-img-wrapper">
                <img src={item.imageUrl} alt={item.name} className="menu-img" />
              </div>
              <div className="menu-info">
                <h3 className="menu-item-title">{item.name}</h3>
                <p className="menu-item-desc">{item.description}</p>
                <div className="menu-price">{item.price.toLocaleString()} đ</div>
                <div className="menu-item-tax">
                  Thuế: {(item.effectiveTaxRate ?? 0).toLocaleString('vi-VN')}%
                  {item.taxRate === undefined || item.taxRate === null ? ' (mặc định)' : ''}
                </div>
                {isAdmin && (
                  <div className="menu-actions">
                    <button className="menu-edit-btn" onClick={() => openForm(item)}>Sửa</button>
                    <button className="menu-delete-btn" onClick={() => handleDelete(item._id)}>Xóa</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {showForm && (
          <div className="menu-form-modal">
            <form className="menu-form" onSubmit={handleSubmit}>
              <h3 className="menu-form-title">{editItem ? 'Sửa món' : 'Thêm món mới'}</h3>
              <input
                type="text"
                name="name"
                placeholder="Tên món"
                value={formData.name}
                onChange={handleChange}
                required
                className="menu-form-input"
              />
              <input
                type="number"
                name="price"
                placeholder="Giá món"
                value={formData.price}
                onChange={handleChange}
                required
                min={1000}
                className="menu-form-input"
              />
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="menu-form-input"
              >
                <option value="cafe">Cafe</option>
                <option value="tea">Trà</option>
                <option value="smoothie">Sinh tố</option>
              </select>
              <input
                type="number"
                name="taxRate"
                placeholder="Thuế suất (%) — bỏ trống: theo mặc định của shop"
                value={formData.taxRate}
                onChange={handleChange}
                min={0}
                max={100}
                step="any"
                className="menu-form-input"
                title="Thuế suất riêng của món. Bỏ trống để dùng thuế mặc định của shop."
              />
              <textarea
                name="description"
                placeholder="Mô tả"
                value={formData.description}
                onChange={handleChange}
                className="menu-form-textarea"
              />
              <div>
                <label htmlFor="image-upload" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  {editItem ? 'Ảnh món (tùy chọn)' : 'Ảnh món *'}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  className="menu-form-file"
                  required={!editItem}
                />
              </div>
              <div className="menu-recipe">
                <label className="menu-recipe-label">Nguyên liệu (định lượng cho 1 phần)</label>
                {materials.length === 0 && (
                  <p className="menu-recipe-hint">Chưa có nguyên liệu. Thêm ở mục "Quản lý kho" trước.</p>
                )}
                {recipe.map((row, idx) => {
                  const mat = materials.find(m => m._id === row.materialId);
                  return (
                    <div className="menu-recipe-row" key={idx}>
                      <select
                        value={row.materialId}
                        onChange={e => updateRecipeRow(idx, 'materialId', e.target.value)}
                      >
                        <option value="">-- chọn nguyên liệu --</option>
                        {materials.map(m => <option key={m._id} value={m._id}>{m.name} ({m.unit})</option>)}
                      </select>
                      <input
                        type="number"
                        min={0}
                        step="any"
                        placeholder="SL"
                        value={row.quantity}
                        onChange={e => updateRecipeRow(idx, 'quantity', e.target.value)}
                      />
                      <span className="menu-recipe-unit">{mat?.unit || ''}</span>
                      <button type="button" className="menu-recipe-remove" onClick={() => removeRecipeRow(idx)}>✕</button>
                    </div>
                  );
                })}
                {materials.length > 0 && (
                  <button type="button" className="menu-recipe-add" onClick={addRecipeRow}>+ Thêm nguyên liệu</button>
                )}
              </div>
              <div className="menu-form-actions">
                <button type="submit" className="menu-form-save-btn" disabled={loading}>{loading ? 'Đang lưu...' : 'Lưu'}</button>
                <button type="button" className="menu-form-cancel-btn" onClick={closeForm}>Hủy</button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default Menu; 